import { Button, Card, Flex, Link, Text } from '@workwhile/ui'
import { YardstickReport } from 'api/typings'
import { RefreshCcw } from 'lucide-react'
import React, { useState } from 'react'

type EducationAndEmploymentCardProps = {
  educationAndEmployment: YardstickReport
  sequence: number
  makeReportUrl: (reportId: string) => string | undefined
  refreshReport: (reportId: string) => Promise<void>
}

const EducationAndEmploymentCard = ({
  educationAndEmployment,
  sequence,
  makeReportUrl,
  refreshReport,
}: EducationAndEmploymentCardProps) => {
  const [refreshFetching, setRefreshFetching] = useState(false)

  const reportUrl =
    educationAndEmployment.reportId && educationAndEmployment.reportId.trim()
      ? makeReportUrl(educationAndEmployment.reportId)
      : undefined

  const refreshVisible = Boolean(
    educationAndEmployment.reportId && educationAndEmployment.reportId.trim()
  )

  const handleClickRefresh = async () => {
    if (
      !educationAndEmployment.reportId ||
      !educationAndEmployment.reportId.trim()
    ) {
      return
    }

    setRefreshFetching(true)
    try {
      await refreshReport(educationAndEmployment.reportId)
    } catch (error) {
      console.error(
        'ERROR OCCURED REFRESHING EDUCATION AND EMPLOYMENT STATUS: ',
        error
      )
    }

    setRefreshFetching(false)
  }

  return (
    <Card shadowless mb={2}>
      <Flex justifyContent="space-between">
        <Text>
          Education and Employment #{sequence}{' '}
          {reportUrl ? (
            <Link
              to={reportUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ marginLeft: '0.5rem' }}
              color="primary"
            >
              (View report)
            </Link>
          ) : null}
        </Text>
        {refreshVisible ? (
          <Button
            kind="small"
            onClick={handleClickRefresh}
            style={{ padding: 4 }}
            variant="text"
            disabled={refreshFetching}
          >
            <RefreshCcw size={14} />
          </Button>
        ) : null}
      </Flex>
      <Flex align="center">
        <Text fontSize={3} style={{ textTransform: 'capitalize' }} mt={1}>
          {educationAndEmployment.status}
        </Text>
      </Flex>
    </Card>
  )
}

type UserEducationAndEmploymentProps = {
  educationAndEmploymentReports?: YardstickReport[]
  makeReportUrl: (reportId: string) => string | undefined
  refreshReport: (reportId: string) => Promise<void>
}

export const UserEducationAndEmployment = ({
  educationAndEmploymentReports,
  makeReportUrl,
  refreshReport,
}: UserEducationAndEmploymentProps) => {
  if (
    !educationAndEmploymentReports ||
    educationAndEmploymentReports.length === 0
  ) {
    return (
      <>
        <Text mb={2}>Education and Employment</Text>
        <Text>
          No education and employment verification found for this user
        </Text>
      </>
    )
  }

  return (
    <>
      <Text mb={2}>Education and Employment</Text>
      {educationAndEmploymentReports.map((educationAndEmployment, index) => (
        <EducationAndEmploymentCard
          key={educationAndEmployment.reportId}
          educationAndEmployment={educationAndEmployment}
          sequence={index + 1}
          makeReportUrl={makeReportUrl}
          refreshReport={refreshReport}
        />
      ))}
    </>
  )
}
